import { type FC } from 'react';
import { getIcon } from '@common/lib/getIcon';
import { toShortNumFormat } from '@common/lib/toShortNumFormat';
import { type PlantTypeText } from '@common/types/plant';
import { type ResourceTypeText } from '@common/types/resource';
import { StringXSmall } from '@components/Typography/Typography';
import { Picture } from '@components/ui/Picture/Picture';
import { Scale } from '@components/ui/Scale/Scale';
import { useAppDispatch, useAppSelector } from '@redux/store';
import cn from 'classnames';

import { useGoToProduct } from '@/common/hooks/go-to-product';
import {
  type ContractDataItems,
  type ContractType,
} from '@/common/types/contract';
import { type GoodsType } from '@/common/types/goods';
import ContractBackground from '@/components/common/ContractBackground/ContractBackground';
import { fixResourceInCustomContract } from '@/redux/reducers/contractReducer';
import { selectProductOfDayByGoodsType } from '@/redux/selectors/contractSelectors';

import css from './styles.module.scss';

import circleCheck from '/img/icons/check-circle.svg';
import shufleLock from '/img/icons/custom_contract/shufleLock.png';

interface ContractClauseProps {
  item: ResourceTypeText | PlantTypeText | GoodsType;
  requirement: ContractDataItems;
  isHorizontal?: boolean;
  type?: ContractType;
  inModal?: boolean;
}

const ContractClause: FC<ContractClauseProps> = ({
  item,
  requirement,
  isHorizontal,
  inModal,
  type,
}) => {
  const productOfDay = useAppSelector((state) =>
    selectProductOfDayByGoodsType(state, item),
  );
  const storage = useAppSelector((state) => state.user.user?.storage);
  const goToProduct = useGoToProduct();
  const itemCapacity =
    storage && item in storage ? storage[item]?.value || 0 : 0;
  const dispatch = useAppDispatch();

  const isComplete = itemCapacity >= requirement.amount;

  const image = getIcon(item);

  const handleClick = (): void => {
    if (type === 'Custom') {
      dispatch(fixResourceInCustomContract(item));
    } else {
      goToProduct(item);
    }
  };

  return (
    <div
      onClick={inModal ? handleClick : undefined}
      className={cn(
        css.clause,
        inModal
          ? 'bg-black/60 backdrop-blur-3xl'
          : 'bg-white/10 backdrop-blur-[32px]',
        'cursor-pointer',
        'flex-1 overflow-hidden relative',
      )}
    >
      {!!productOfDay && (
        <ContractBackground
          type={productOfDay.type === 'premium' ? 'purple' : 'green'}
        />
      )}
      {requirement.fixed && (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <img src={shufleLock} width={32} alt="lock" />
        </div>
      )}
      {image && (
        <Picture
          className={cn(
            css.picture,
            isHorizontal ? 'h-full' : 'w-full',
            requirement.fixed && 'opacity-[0.5]',
          )}
          webp={image?.webp}
          img={image.png}
          alt="item"
        />
      )}
      {isComplete && (
        <img
          className="absolute right-[3px] top-[3px]"
          src={circleCheck}
          alt="completed"
        />
      )}
      <div
        className={cn(
          'w-full flex flex-col gap-1 rounded-b-lg overflow-hidden items-center',
          requirement.fixed && 'opacity-[0.5]',
        )}
      >
        <div className={cn('p-1 rounded-md bg-main-bg/60 backdrop-blur w-max')}>
          <StringXSmall
            className="flex items-center gap-[2px] text-white"
            weight="bold"
          >
            <span className={isComplete ? '!text-accent' : '!text-red'}>
              {toShortNumFormat(itemCapacity)}
            </span>
            <span>/</span>
            <span>{toShortNumFormat(requirement.amount)}</span>
          </StringXSmall>
        </div>
        <Scale
          className="!bg-transparent"
          height={3}
          percentage={
            itemCapacity > requirement.amount
              ? 100
              : (itemCapacity * 100) / requirement.amount
          }
        />
      </div>
    </div>
  );
};

export { ContractClause };
